/* TanStack Query hooks for the AQ devices REST surface.
 *
 * Endpoints (per vertical-aq plugin contract):
 *   GET /devices         → DeviceSummary[]
 *   GET /devices/:id     → DeviceDetail
 *
 * Schema names align with the Phase-2 port plan; if the actual sp-api
 * shape differs, the only file that needs updating is this one (and the
 * types here). Routes/components stay clean. */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '../lib/api';

export interface DeviceSummary {
  device_id: string;
  device_name: string;
  device_type: string;
  /** ISO-8601 timestamp of latest reading (or null when never reported). */
  last_seen_at: string | null;
  /** Latest alert tier: 'ok' | 'watch' | 'warn' | 'alarm' — server-derived. */
  status: 'ok' | 'watch' | 'warn' | 'alarm' | 'unknown';
  location: string | null;
}

export interface DeviceDetail extends DeviceSummary {
  serial_number: string;
  firmware_version: string | null;
  hardware_version: string | null;
  provisioned_at: string;
  /** Latest reading values keyed by sensor name (raw, unscaled). */
  latest_values: Record<string, number | null> | null;
}

export function useDevices(): UseQueryResult<DeviceSummary[]> {
  return useQuery<DeviceSummary[]>({
    queryKey: ['devices'],
    queryFn: () => api.get<DeviceSummary[]>('/devices'),
  });
}

export function useDevice(deviceId: string | undefined): UseQueryResult<DeviceDetail> {
  return useQuery<DeviceDetail>({
    queryKey: ['device', deviceId],
    queryFn: () => api.get<DeviceDetail>(`/devices/${deviceId}`),
    enabled: !!deviceId,
  });
}
